import React, { useState } from 'react';
import { FileText, Download } from 'lucide-react';
import { Button, Card, FileUploadZone, ProgressBar, Alert, Badge, ToolActionButtons, NoteCard } from '../shared';
import { getDocument, GlobalWorkerOptions } from 'pdfjs-dist';
import { saveAs } from 'file-saver';
import { toast } from 'react-toastify';
import mammoth from 'mammoth';
import '../../styles/design-system.css';

// Set up PDF.js worker
GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js';

const PDFtoWORD = () => {
    const [file, setFile] = useState(null);
    const [isProcessing, setIsProcessing] = useState(false);
    const [progress, setProgress] = useState(0);
    const [pageCount, setPageCount] = useState(0);

    const handleFileSelected = async (files) => {
        if (files && files.length > 0) {
            const selectedFile = files[0];
            setFile(selectedFile);

            try {
                const arrayBuffer = await selectedFile.arrayBuffer();
                const loadingTask = getDocument({ data: arrayBuffer });
                const pdf = await loadingTask.promise;
                setPageCount(pdf.numPages);
                toast.success(`PDF loaded: ${pdf.numPages} pages`);
            } catch (error) {
                toast.error(`Failed to load PDF: ${error.message}`);
            }
        }
    };

    const convertToWord = async () => {
        if (!file) return;

        try {
            setIsProcessing(true);
            setProgress(0);

            // Note: This is a simplified conversion
            // Full PDF to Word conversion requires more sophisticated text extraction
            toast.info('Converting PDF to Word format...');
            setProgress(50);

            // For now, we'll create a simple Word document with extracted text
            // In production, you'd use a more sophisticated conversion library
            const arrayBuffer = await file.arrayBuffer();
            const loadingTask = getDocument({ data: arrayBuffer });
            const pdf = await loadingTask.promise;

            let extractedText = '';
            for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
                setProgress(50 + (pageNum / pdf.numPages) * 40);
                const page = await pdf.getPage(pageNum);
                const textContent = await page.getTextContent();
                const pageText = textContent.items.map(item => item.str).join(' ');
                extractedText += `Page ${pageNum}\n${pageText}\n\n`;
            }

            // Create a simple text file (Word conversion would require docx library)
            const blob = new Blob([extractedText], { type: 'text/plain' });
            const filename = file.name.replace('.pdf', '.txt');
            
            setProgress(100);
            saveAs(blob, filename);
            toast.success('Conversion completed! Note: This creates a text file. Full Word conversion coming soon.');
        } catch (error) {
            console.error('Conversion error:', error);
            toast.error(`Conversion failed: ${error.message}`);
        } finally {
            setIsProcessing(false);
        }
    };

    return (
        <div>
            {/* Action Buttons */}
            <ToolActionButtons
                onAdd={() => {
                    document.querySelector('input[type="file"]')?.click();
                }}
                notificationCount={file ? 1 : 0}
            />

            <div className="tool-header">
                <h1 className="tool-title">
                    <FileText size={36} style={{ display: 'inline', marginRight: 'var(--spacing-3)', color: 'var(--color-accent)' }} />
                    PDF to WORD
                </h1>
                <p className="tool-description">
                    Convert PDF documents to Microsoft Word format (DOCX).
                </p>
            </div>

            {!file ? (
                <FileUploadZone
                    onFilesSelected={handleFileSelected}
                    acceptedTypes={['application/pdf']}
                    multiple={false}
                    title="Drop PDF file here"
                    description="or click to browse"
                />
            ) : (
                <>
                    <Alert variant="success" title="File Loaded">
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)' }}>
                                <FileText size={16} />
                                <span>{file.name}</span>
                                {pageCount > 0 && <Badge variant="info">{pageCount} page{pageCount !== 1 ? 's' : ''}</Badge>}
                            </div>
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                    setFile(null);
                                    setPageCount(0);
                                }}
                            >
                                Change File
                            </Button>
                        </div>
                    </Alert>

                    {isProcessing && (
                        <div style={{ marginTop: 'var(--spacing-6)' }}>
                            <ProgressBar value={progress} striped showLabel />
                        </div>
                    )}

                    <div style={{ marginTop: 'var(--spacing-6)' }}>
                        <Button
                            variant="primary"
                            size="lg"
                            onClick={convertToWord}
                            disabled={isProcessing}
                            loading={isProcessing}
                            fullWidth
                        >
                            <Download size={20} />
                            Convert to Word
                        </Button>
                    </div>

                    <NoteCard variant="info">
                        <strong>Note:</strong> Full DOCX conversion is in development. Currently extracts text content.
                    </NoteCard>
                </>
            )}
        </div>
    );
};

export default PDFtoWORD;

